import { FeatureFlag } from '@ps/web-core-store';
import type { FeatureState } from '@ps/web-core-store';
import classNames from 'clsx';
import { intersection } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

import Translation from '@swf/components/Translation/Translation';
import InfoIcon from '@swf/core/assets/casino/info.svg';
import Arrow from '@swf/core/assets/icons/arrow.svg';
import { select } from '@swf/core/store';
import { type CasinoTable } from '@swf/core/types/CasinoGame';
import type { TranslationTag } from '@swf/core/types/i18n';

import {
  TXTACL_Bonus_Features,
  TXTACL_Wilds,
  TXTWCL_Bonus_Rounds,
  TXTWCL_Create_winning_combinations_th_ELL,
  TXTWCL_Free_Spins,
  TXTWCL_Multipliers,
  TXTWCL_Multiply_betsX_line_wins_and_t_ELL,
  TXTWCL_Spin_the_reels_and_multiply_yo_ELL,
  TXTWCL_Trigger_the_bonus_round_to_spi_ELL
} from './BonusFeatures.messages.json';
import styles from './BonusFeatures.scss';
import BonusRound from './assets/bonus-round.svg';
import FreeSpins from './assets/free-spins.svg';
import Multipliers from './assets/multipliers.svg';
import Wilds from './assets/wilds.svg';

interface RenderField {
  text: TranslationTag;
  description: TranslationTag;
  icon: React.FC<React.SVGAttributes<SVGElement>>;
  flag?: string;
}

const lookup = {
  multiplier: 'multiplier',
  wilds: 'wilds'
};

const RenderFields: Record<string, RenderField> = {
  multiplier: {
    icon: Multipliers,
    text: TXTWCL_Multipliers,
    description: TXTWCL_Multiply_betsX_line_wins_and_t_ELL,
    flag: FeatureFlag.CasinoGameFeatureMultiplier
  },
  bnsRnd: {
    icon: BonusRound,
    text: TXTWCL_Bonus_Rounds,
    description: TXTWCL_Trigger_the_bonus_round_to_spi_ELL
  },
  fsbTitle: {
    icon: FreeSpins,
    text: TXTWCL_Free_Spins,
    description: TXTWCL_Spin_the_reels_and_multiply_yo_ELL
  },
  wilds: {
    icon: Wilds,
    text: TXTACL_Wilds,
    description: TXTWCL_Create_winning_combinations_th_ELL,
    flag: FeatureFlag.CasinoGameFeatureWilds
  }
};

const eligibleFeatures = (fields?: string[]) => intersection(fields, Object.keys(RenderFields));

interface Props {
  className?: string;
  properties: Partial<CasinoTable['properties']>;
  featureNames: CasinoTable['featureNames'];
  onClick?(): void;
  onClose?(): void;
}

const BonusFeatures: React.FC<Props> = ({
  className,
  featureNames,
  onClick,
  onClose,
  properties
}) => {
  const propFeatures = Object.keys(lookup)
    .map((key) => (properties?.[key] ? lookup[key] : undefined))
    .filter((v) => !!v) as string[];

  const enabledFeatures = useSelector(select.feature.get) as FeatureState;

  const allFeatures = (featureNames || []).concat(propFeatures);

  const features = eligibleFeatures(allFeatures).filter((toShow) => {
    const { flag } = RenderFields[toShow];

    return flag ? enabledFeatures[flag] : true;
  });

  const withFeatures = features?.length;
  return withFeatures ? (
    <div className={classNames(styles.container, onClose && styles.popup, className)}>
      <div className={classNames(styles.header)}>
        {onClose && <Arrow className={styles.arrow} onClick={onClose} />}
        <span>
          <Translation {...TXTACL_Bonus_Features} />:
        </span>
        {onClick && <InfoIcon className={styles.info} onClick={onClick} />}
      </div>
      <div className={styles.featureList}>
        {features.map((key: string) => {
          const entry = RenderFields[key];

          return (
            <React.Fragment key={key}>
              {entry.icon && React.createElement(entry.icon)}
              <span className={styles.text}>
                <Translation {...entry.text} />
              </span>
              {onClose && (
                <span className={styles.description}>
                  <Translation {...entry.description} />
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  ) : null;
};

export default BonusFeatures;
